/**
 * CompactNestedSections — the body of a DRILLED subquery view inside the compact
 * VQE popover (Task 8). Mounted only when the compact drill stack is non-empty
 * (`drill.active`), so it can ALWAYS call `useCompactQueryModel` unconditionally
 * (React hooks rule) while the parent toggles its mount.
 *
 * It runs the same model derivation as the base popover body, but over the
 * drilled subquery's edit context (`deriveSubqueryEditContext`). When the
 * subquery body is itself a shape the compact surface can't safely edit (a
 * nested CTE / set-op / window / derived-table), it degrades to the same
 * one-line notice + "Open full editor" CTA as the base.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3.
 */

import { useEffect } from 'react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import type { DrillEntry } from '../../types';
import type { SubqueryWiring } from '../../render/select/SelectRenderer';
import type { CompactDrill } from '../useCompactDrill';
import { useCompactQueryModel } from '../useCompactQueryModel';
import { vqeCopy } from '../../ux/vqeCopy';
import { CompactDegradeNotice } from './CompactDegradeNotice';
import { CompactEditSections } from './CompactEditSections';

/**
 * Build the compact WHERE-subquery drill wiring for a given (synthetic or real)
 * statement id. `onDrill` pushes onto the LOCAL compact drill stack (never the
 * global workspace drillStack), so deeper subqueries can drill again.
 */
export function buildCompactSubqueryCtx(
  statementId: string,
  tabId: string,
  drill: CompactDrill,
  onEditAsSql: () => void,
): SubqueryWiring {
  return {
    statementId,
    drillTabId: tabId,
    outerAliases: [],
    mergedAliases: [],
    onEditAsSql,
    onDrill: (entry: DrillEntry) =>
      drill.push({ bodyStart: entry.bodyStart, bodyEnd: entry.bodyEnd, label: entry.label }),
  };
}

export interface CompactNestedSectionsProps {
  /** The drilled subquery edit context (from `deriveSubqueryEditContext`). */
  readonly editContext: VqeEditContext;
  readonly options: ParseOptions;
  /** Drill wiring keyed on the drilled statement id (enables depth > 1). */
  readonly subqueryCtx: SubqueryWiring;
  readonly onOpenFull: () => void;
  /**
   * § 1.5 — reports whether THIS nested drill is showing its own degrade card,
   * so the popover footer can hide its "Open full editor" (the card owns the
   * CTA while it's on screen). Fires on every degrade change and resets to
   * `false` on unmount (drill deactivated / popped).
   */
  readonly onDegradedChange?: (degraded: boolean) => void;
}

export function CompactNestedSections({
  editContext,
  options,
  subqueryCtx,
  onOpenFull,
  onDegradedChange,
}: CompactNestedSectionsProps) {
  // The drilled body is always a query body (resolveDrilledSubqueryBodyEnd only
  // accepts SELECT/WITH/VALUES), so classify its degrade as a SELECT — that
  // surfaces nested structural shapes (CTE / set-op / window / derived-table)
  // as the right degrade reason rather than a blanket fallback.
  const model = useCompactQueryModel(editContext, 'select', options);
  const degraded = model.degrade.degraded || !model.outline;
  useEffect(() => {
    onDegradedChange?.(degraded);
    return () => onDegradedChange?.(false);
  }, [degraded, onDegradedChange]);

  if (degraded) {
    return (
      <CompactDegradeNotice
        reason={model.degrade.degraded ? model.degrade.reason : 'unsupported'}
        message={
          model.degrade.degraded ? model.degrade.message : vqeCopy.degrade.subqueryFallback
        }
        onOpenFull={onOpenFull}
      />
    );
  }

  return (
    <CompactEditSections
      bufferText={model.bufferText}
      outline={model.outline}
      options={options}
      editContext={editContext}
      subqueryCtx={subqueryCtx}
      // A drilled body may itself declare a WITH (a CTE whose body is another
      // `WITH … SELECT`, or a subquery with its own CTEs). It classifies as
      // 'select' above, so the model carries its outline — thread it so the
      // nested frame gets the same strip, and drilling deeper works through
      // this same wiring.
      cteOutline={model.cteOutline}
    />
  );
}
